import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";

const painPoints = [
  {
    title: "以多報少或投保不一致",
    desc: "國稅局、勞保局資料可交叉比對，差額易被查，甚至可能衍生補繳與爭議。",
  },
  {
    title: "非工資項目被認定為工資",
    desc: "車馬費、津貼、固定獎金若未妥善設計，恐需補提保費與勞退。",
  },
  {
    title: "契約條款與團險責任未排除",
    desc: "發生職災或爭議時，企業仍可能承擔額外責任。",
  },
  {
    title: "家族 / 主管聘用關係混淆",
    desc: "未善用委任模式，彈性、稅務與保費空間都可能被鎖死。",
  },
  {
    title: "特休與請假制度不合規",
    desc: "流程不清、繁忙期拒絕特休，容易違反勞基法並引發爭議。",
  },
  {
    title: "工作規則沿用公版",
    desc: "公版規則未必符合企業實務，遇到爭議時可能對公司不利。",
  },
];

const coreServices = [
  {
    number: "01",
    title: "薪資結構重整｜薪資架構優化",
    desc: "合法優化薪資結構、人事成本並降低勞檢風險。",
    icon: "ri-wallet-3-line",
    items: [
      "薪資結構與非工資項目分類",
      "津貼獎金設計",
      "投保差異管理",
      "勞退與保費風險檢查",
    ],
  },
  {
    number: "02",
    title: "勞動合約與工作規則優化",
    desc: "量身打造符合法規的合約條款，保護企業權益。",
    icon: "ri-file-shield-line",
    items: [
      "勞動契約檢視",
      "工作規則優化",
      "特休、請假、加班制度設計",
      "企業內部規範與文件化",
    ],
  },
  {
    number: "03",
    title: "企業主保障與員工福利制度",
    desc: "協助企業主把個人風險、企業風險與員工福利制度整合。",
    icon: "ri-shield-user-line",
    items: [
      "團體保險規劃",
      "負責人與關鍵人保障",
      "留才福利制度",
      "接班、股權與傳承架構",
    ],
  },
];

const steps = [
  {
    number: "01",
    title: "現況盤點",
    desc: "企業規模、薪資結構、人事制度與保險配置。",
  },
  {
    number: "02",
    title: "風險診斷",
    desc: "找出法遵、人事、保障與成本風險。",
  },
  {
    number: "03",
    title: "策略設計",
    desc: "提出薪資、契約、福利與保障整合方案。",
  },
  {
    number: "04",
    title: "文件落地",
    desc: "協助建立制度、表單、規則與溝通話術。",
  },
  {
    number: "05",
    title: "執行追蹤",
    desc: "依企業狀況定期檢視。",
  },
  {
    number: "06",
    title: "成效回報",
    desc: "提供節省金額、法遵指標、爭議件數趨勢等可量化報告。",
  },
];

export default function Enterprise() {
  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="企業門神 | 企業風險管理與傳承規劃 | 保險門神"
        description="企業門神專為企業主、負責人、合夥人設計，涵蓋薪資結構與投保管理、勞動合約與工作規則優化、員工福利與團險制度、企業主風險控管、接班股權與傳承架構。"
        keywords="企業保險,企業風險管理,團險規劃,企業傳承,薪資結構優化,保險門神"
        canonicalPath="/services/enterprise"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "企業門神 - 企業風險管理與傳承規劃",
          "description": "穩企業、降風險、優化成本。從薪資結構、勞動合約、員工福利、企業主保障到接班傳承，建立企業長久運行的制度防線。",
          "provider": {
            "@type": "FinancialService",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          },
          "url": "__SITE_URL__/services/enterprise",
          "areaServed": { "@type": "Country", "name": "Taiwan" },
          "serviceType": "企業風險管理、薪資結構優化與傳承規劃"
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20corporate%20business%20background%20with%20golden%20architectural%20lines%20on%20deep%20black%20canvas%2C%20subtle%20skyscraper%20silhouettes%20formed%20by%20flowing%20gold%20light%2C%20premium%20enterprise%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20metallic%20gold%20accents%2C%20modern%20and%20powerful%20atmosphere%2C%204K%20quality&width=1920&height=600&seq=105&orientation=landscape"
              alt="企業門神 Hero 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[0.9375rem] mb-6">
              <Link to="/" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                首頁
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <Link to="/services" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                服務項目
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <span className="text-brand-gold">企業門神</span>
            </div>

            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
              企業門神
            </h1>
            <p className="text-brand-gold text-[1.1875rem] font-medium mb-4">
              守護企業主的事業
            </p>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[800px]">
              穩企業、降風險、優化成本。從薪資結構、勞動合約、員工福利、企業主保障到接班傳承，建立企業長久運行的制度防線。
            </p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  企業主常見痛點
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {painPoints.map((point, index) => (
                <ScrollReveal key={index} delay={index * 80}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.9375rem] md:text-[1rem] shrink-0">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="pt-1">
                        <p className="text-white text-[1.0625rem] md:text-[1.1875rem] leading-[1.6] mb-1.5 font-medium">
                          {point.title}
                        </p>
                        <p className="text-brand-text-secondary text-[1rem] leading-[1.7]">
                          {point.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Core Services Banner */}
        <section className="relative h-[260px] md:h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20corporate%20background%20with%20golden%20architectural%20building%20lines%20and%20enterprise%20tower%20silhouettes%20on%20deep%20black%20canvas%2C%20subtle%20network%20connection%20patterns%20made%20of%20gold%20metallic%20light%2C%20premium%20business%20consulting%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%20with%20warm%20golden%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=400&seq=204&orientation=landscape"
            alt="企業核心服務意象"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/70" />
        </section>

        {/* Core Services */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  三大核心服務
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {coreServices.map((service, index) => (
                <ScrollReveal key={service.number} delay={index * 120}>
                  <div
                    className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-gold/10">
                        <span className="w-6 h-6 flex items-center justify-center text-brand-gold text-xl">
                          <i className={service.icon} />
                        </span>
                      </div>
                      <span className="text-brand-gold font-bold text-[1rem] md:text-[1.125rem]">
                        {service.number}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] leading-[1.45] mb-2">
                      {service.title}
                    </h3>
                    <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75] mb-5">
                      {service.desc}
                    </p>
                    <ul className="space-y-2.5">
                      {service.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-brand-text-secondary text-[1rem] md:text-[1.0625rem]">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  合作流程
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.number} delay={index * 80}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center shrink-0">
                        <span className="text-brand-gold font-bold text-[0.9375rem] md:text-[1.0625rem]">
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-[1.125rem] md:text-[1.25rem]">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.75]">
                      {step.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  真實案例與成果
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  client: "30 人小型企業",
                  pain: "薪資結構以多報少，勞檢風險高，員工流動率大。",
                  solution: "重整薪資架構，合法優化非工資項目，重新設計勞動契約與工作規則。",
                  result: "年省勞健保與勞退費用約 28 萬，員工滿意度提升，零勞檢爭議。",
                  tag: "薪資結構重整",
                },
                {
                  client: "100 人製造業",
                  pain: "團體保險僅覆蓋基本醫療，職災與關鍵人保障不足。",
                  solution: "重新規劃團體保險方案，加入負責人與關鍵人保障，設計留才福利。",
                  result: "保障範圍擴大 60%，員工留任率提升 15%，企業主風險完全覆蓋。",
                  tag: "企業保障優化",
                },
                {
                  client: "家族企業（二代接班）",
                  pain: "家族成員與主管聘用關係模糊，股權與接班架構未明確。",
                  solution: "釐清委任與僱傭關係，設計股權傳承架構與接班培養計畫。",
                  result: "家族治理制度化，接班爭議風險降為零，企業永續經營架構確立。",
                  tag: "接班傳承",
                },
              ].map((c, index) => (
                <ScrollReveal key={c.tag} delay={index * 120}>
                  <div className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="inline-block border border-brand-gold/40 text-brand-gold text-[0.875rem] font-medium px-3 py-1 rounded-full">
                        {c.tag}
                      </span>
                    </div>
                    <p className="text-white font-semibold text-[1.125rem] md:text-[1.25rem] mb-4">
                      {c.client}
                    </p>

                    <div className="space-y-4 mb-6 flex-1">
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">面臨問題</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.pain}
                        </p>
                      </div>
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">解決方案</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.solution}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-brand-border pt-5 mt-auto">
                      <p className="text-brand-gold font-bold text-[1.0625rem] md:text-[1.125rem] leading-[1.7]">
                        {c.result}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Quick Q&A */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  常見問題
                </h2>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="max-w-[800px] space-y-4">
                {[
                  {
                    q: "企業健診需要準備什麼資料？",
                    a: "建議準備近一年的薪資明細、勞動契約、工作規則、團體保險保單與員工名冊。我們會在初談時提供詳細清單。",
                  },
                  {
                    q: "調整薪資結構會影響員工權益嗎？",
                    a: "不會。我們的所有調整建議都以合法合規為前提，目標是讓企業與員工達到雙贏，而非單方面壓縮成本。",
                  },
                  {
                    q: "企業門神與一般保險業務有何不同？",
                    a: "我們不只賣保單，而是從薪資結構、人事制度、法遵風險、企業主保障與傳承架構做全面檢視與整合。",
                  },
                  {
                    q: "多久可以看到具體改善成效？",
                    a: "法遵與制度面的調整通常一至三個月即可完成；成本優化與風險降低的效果，則會在後續季度報告中清楚呈現。",
                  },
                ].map((faq, i) => (
                  <details
                    key={i}
                    className="bg-brand-black border border-brand-border rounded-xl group transition-all duration-300 hover:border-brand-gold/40"
                  >
                    <summary className="flex items-start justify-between gap-4 p-5 md:p-6 text-left cursor-pointer list-none">
                      <div className="flex items-start gap-3 md:gap-4">
                        <span className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.875rem] md:text-[1rem] shrink-0 mt-0.5">
                          Q{i + 1}
                        </span>
                        <span className="text-white text-[1.0625rem] md:text-[1.1875rem] font-medium leading-[1.55] pt-1">
                          {faq.q}
                        </span>
                      </div>
                      <span className="w-6 h-6 flex items-center justify-center text-brand-gold shrink-0 mt-1 group-open:rotate-180 transition-transform duration-300">
                        <i className="ri-arrow-down-s-line text-xl" />
                      </span>
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pl-16 md:pl-[4.25rem]">
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.125rem] leading-[1.75]">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20fortress%20and%20enterprise%20tower%20silhouettes%20on%20deep%20black%20canvas%2C%20subtle%20protective%20golden%20dome%20shielding%20business%20structures%2C%20premium%20corporate%20guardian%20brand%20aesthetic%2C%20commanding%20and%20secure%20atmosphere%20with%20warm%20gold%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=500&seq=212&orientation=landscape"
              alt="企業門神 CTA 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
          </div>
          <div className="container-main relative z-10">
            <ScrollReveal className="text-center max-w-[700px] mx-auto">
              <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-3">
                企業要走得久，制度要先站穩。
              </h2>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] mb-8">
                預約企業風險健診，從薪資、人事、保障與傳承一次檢視。
              </p>
              <a
                href="https://lin.ee/o51JBHX"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-10 py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
              >
                <i className="ri-line-fill" />
                加入 LINE 預約企業諮詢
              </a>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}